import { registerActions } from "../actionRegistry";
/**
 * Settings Save Functions
 *
 * Functions for saving user settings, artwork data, and moderation states.
 */

import type { ANIMAL_TYPE, BUILDING_TYPE, PEOPLE_COUNT } from "@brave-new-world/shared";
import { api } from "../api";
import { modal_open_what } from "../globalState";

// Local timer state
let biotimer: ReturnType<typeof setTimeout> | undefined;
import { toastSuccess, toastError } from "../ui/messages";
import { closeModalLast, refreshModal } from "../ui/modal";
import { next } from "./navigation";
import {
  getElementValue,
  validateEmail,
  getDomElement,
  setText,
  setHtml,
  setStyle,
  hide,
  queryAll,
} from "../utils/dom";

export async function savesettings(): Promise<void> {
  setStyle("login_button", "opacity", "0.3");

  // Reseller role flags are deliberately NOT sent: they are admin-owned (set via
  // users.add) and this form never rendered the `settings_role_*` checkboxes it
  // used to read, so it was posting `false` for all three and wiping them.
  const paypal = getDomElement<HTMLInputElement>("settings_paypal")?.value ?? "";
  const username = getDomElement<HTMLInputElement>("settings_username")?.value ?? "";

  // Resellers only — the field is absent for everyone else, and an absent field must be sent
  // as `undefined` so the server leaves the column alone rather than resetting it to Classic.
  const downloaderEl = getDomElement<HTMLSelectElement>("settings_downloader");
  const downloader =
    downloaderEl?.value === "ENHANCED" || downloaderEl?.value === "CLASSIC"
      ? downloaderEl.value
      : undefined;
  const downloaderChanged =
    downloader !== undefined && downloader !== downloaderEl?.dataset.initial;

  if (paypal !== "" && !validateEmail(paypal)) {
    setStyle("login_button", "opacity", "1");
    toastError("Paypal address must be the e-mail you have registered on your Paypal account");
    return;
  }

  try {
    // saveSettings reports username rejections ("already in use", too short, bad characters)
    // in its `status` field rather than by throwing — the username change is one part of a
    // save that otherwise succeeded. Ignoring it meant a rejected username still closed the
    // modal with "Settings saved", so the user was told their change had been applied.
    const res = await api.users.saveSettings({
      name: getElementValue("settings_name"),
      username,
      paypal,
      phone: getElementValue("settings_phone"),
      country: getElementValue("settings_country"),
      ...(downloader !== undefined ? { downloader } : {}),
    });
    if (res.status !== "OK") {
      setStyle("login_button", "opacity", "1");
      toastError(res.status);
      return;
    }
    closeModalLast();
    toastSuccess("Settings saved");

    // Switching downloader changes which cart UI the page renders, and that is decided
    // server-side when the page is built — so reload rather than leave the reseller looking
    // at the downloader they just switched away from.
    if (downloaderChanged) {
      setTimeout(() => location.reload(), 600);
    }
  } catch (err: unknown) {
    alert((err as Error).message);
    setStyle("login_button", "opacity", "1");
  }
}

/**
 * The edit-artwork form, owned by EditPhotoModal.
 *
 * Previously read out of the DOM by id, including three radio groups found with
 * `input[name=…]:checked` — which resolves against the whole document, so a
 * second modal rendering the same group names would have won.
 */
export interface ArtworkFormValues {
  title: string;
  tags: string;
  category: string;
  /** 1 = crop, 2 = stretch. */
  design: 1 | 2;
  people: (typeof PEOPLE_COUNT)[number];
  buildings: (typeof BUILDING_TYPE)[number];
  animals: (typeof ANIMAL_TYPE)[number];
  /** Legacy 0/1/2 code from the select. */
  qcCode: string;
  muralgrade: string;
  grade: string;
  badprint: boolean;
  blocksales: boolean;
}

export async function saveartwork(id: string, values: ArtworkFormValues): Promise<void> {
  // The qc <select> emits the legacy 0/1/2 codes → map to the Artwork_quality_control enum.
  const qc = values.qcCode === "2" ? "GOOD" : values.qcCode === "1" ? "BAD" : "NOT_CHECKED";

  try {
    await api.artwork.saveArtwork({
      id,
      title: values.title,
      design: values.design,
      tags: values.tags,
      category: values.category,
      people: values.people,
      buildings: values.buildings,
      animals: values.animals,
      qc,
      muralgrade: values.muralgrade,
      grade: values.grade,
      badprint: values.badprint,
      blocksales: values.blocksales,
    });
    closeModalLast();
    toastSuccess("Artwork metadata saved");
    setTimeout(() => {
      refreshModal();
    }, 500);
    setTimeout(() => {
      refreshWallPrev();
    }, 1500);
  } catch (err: unknown) {
    toastError(err instanceof Error ? err.message : "Could not save artwork");
  }
}

export function refreshWallPrev(): void {
  const wallPrev = getDomElement<HTMLImageElement>("wall-prev");
  if (wallPrev) {
    wallPrev.src = wallPrev.src + "&rand=" + String(Math.floor(Math.random() * 10000));
  }
}

export async function savecat(id: string): Promise<void> {
  const catEl = getDomElement<HTMLSelectElement>("savecat_" + id);
  if (!catEl) return;

  try {
    await api.artwork.saveCategory({ id: Number(id), cat: Number(catEl.value) });
    const whata = modal_open_what.split(":");
    if (whata[0] === "view" && getElementValue("img-next") !== "") {
      next();
    }
    toastSuccess("Category changed");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function savebio(): Promise<void> {
  try {
    await api.artists.saveBio({
      bio: getElementValue("portfolioreview-biography"),
      instagram: getElementValue("instagram"),
    });
    setHtml("activate-biography-status", '<span class="icon-green">Biography saved!</span>');
    setTimeout(function () {
      setText("activate-biography-status", "");
    }, 2000);
    clearTimeout(biotimer);
    biotimer = setTimeout(function () {
      hide("activate-biography-button");
    }, 2000);
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function msLater(): Promise<void> {
  try {
    await api.metadata.saveForLater({ id: Number(getElementValue("ms-id")) });
    refreshModal();
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export function requestslots(): void {
  closeModalLast();
  toastSuccess(
    "Your request to increase download slots have been sent. You will be notified by e-mail and notification when a decision is taken"
  );
}

export async function savegreetname(id: string, name: string): Promise<void> {
  try {
    await api.artists.saveGreetName({ id: Number(id), name });
    toastSuccess("Greeting name saved");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function savealtname(id: string, name: string): Promise<void> {
  try {
    await api.artists.saveAltName({ id: Number(id), name });
    toastSuccess("Alternate name saved");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function blockartist(id: string, block_upload: boolean): Promise<void> {
  try {
    await api.artists.blockUpload({ id, block_upload });
    toastSuccess(block_upload ? "Artist blocked" : "Artist unblocked");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function saveextendedearnings(id: string, ext_earn: boolean): Promise<void> {
  try {
    await api.artists.saveExtEarn({ id: Number(id), extEarn: ext_earn });
    toastSuccess(ext_earn ? "Detailed earnings enabled" : "Detailed earnings disabled");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function savehidecountry(id: string, hide: boolean): Promise<void> {
  // The procedure takes 1/0, not a boolean — the wire encoding stays here so the
  // modal does not have to know it.
  const shc_hide = hide ? 1 : 0;
  try {
    await api.artists.saveHide({ id: Number(id), hide: shc_hide });
    toastSuccess(shc_hide === 1 ? "Country now hidden" : "Country no longer hidden");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function savealtdesc(id: string, name: string): Promise<void> {
  try {
    await api.artists.saveAltDesc({ id: Number(id), name });
    toastSuccess("Alternate category saved");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function setartistthumb(pic_id: string, artist_id: string): Promise<void> {
  try {
    await api.artists.setThumb({ artistId: Number(artist_id), picId: pic_id });
    closeModalLast();
    toastSuccess("Artist thumb set");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function savelistorder(id: string): Promise<void> {
  const i_order: number[] = [];
  // `.reorderlist-img`, NOT `img.reorderlist-img`: the tile is a div wrapping the thumb, so
  // that an artwork with no S3 thumb is still a draggable element with an id instead of
  // vanishing from the order it is supposed to be saving.
  for (const img of queryAll<HTMLElement>(".reorderlist-img")) {
    const raw_id = Number(img.id.replace("artwork-", ""));
    if (raw_id > 0) {
      i_order.push(raw_id);
    }
  }

  try {
    await api.lightbox.saveOrder({ id, order: i_order });
    location.href = "/list/" + id;
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

const SEGMENT_VALUES = ["UNSEGMENTED", "A", "B", "C", "D", "FEATURED"] as const;

/** Responsible / segment / signed-by, saved together by any of their three buttons. */
export interface ArtistExtras {
  responsible: string;
  segment: string;
  signedby: string;
}

export async function saveextras(id: string, extras: ArtistExtras): Promise<void> {
  // An unrecognised segment falls back rather than reaching the enum column —
  // the select is populated from the server, but the value is still a string.
  const segment = (SEGMENT_VALUES as readonly string[]).includes(extras.segment)
    ? (extras.segment as (typeof SEGMENT_VALUES)[number])
    : "UNSEGMENTED";
  try {
    await api.artists.saveExtras({
      id: Number(id),
      responsible: Number(extras.responsible),
      segment,
      signedby: Number(extras.signedby),
    });
    toastSuccess("Responsible/segment/signed by saved");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function saveusername(id: string, name: string): Promise<void> {
  try {
    await api.artists.saveUsername({ id: Number(id), name });
    toastSuccess("Username saved");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

registerActions({
  refreshWallPrev,
  savecat,
  savebio,
});
